import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PendingSignaturePlacementDto } from '@dealstack/shared/lib/generated';
import type {
	CanvasPlacement,
	PdfBox,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/PlacementCanvas.types';
import { expectedAspectRatio } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/placement.utils';
import {
	buildSlotMetaMap,
	defaultBoxFor,
	isPlacementSetComplete,
	rescalePlacement,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/signaturePlacementShared';
import type { PendingSlot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/clientSideSlots.types';
import type { PendingSignaturePlacementResult } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/usePendingSignaturePlacement.types';
import { findOverlappingKeys } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/placementCollision.utils';

const toDto = (
	slots: PendingSlot[],
	placementsByKey: Map<string, CanvasPlacement>
): PendingSignaturePlacementDto[] => {
	const out: PendingSignaturePlacementDto[] = [];
	slots.forEach((slot) => {
		const placement = placementsByKey.get(slot.key);
		if (!placement) {
			return;
		}
		out.push({
			roleType: slot.roleType,
			roleName: slot.roleName,
			isWitness: slot.isWitness,
			signatureIndex: slot.signatureIndex,
			pageNum: placement.pageNum,
			x: placement.pdfBox.x,
			y: placement.pdfBox.y,
			width: placement.pdfBox.width,
			height: placement.pdfBox.height,
		});
	});
	return out;
};

/**
 * Pending-state sister of `useSignaturePlacement`: takes a client-derived slot list
 * (no template yet) but mirrors its output so the canvas renders unchanged. Emits the derived
 * PendingSignaturePlacementDto[] to `onPlacementsChange` synchronously on every change.
 */
export const usePendingSignaturePlacement = (
	slots: PendingSlot[],
	showMetadata: boolean,
	onPlacementsChange: (placements: PendingSignaturePlacementDto[]) => void
): PendingSignaturePlacementResult => {
	const slotByKey = useMemo(() => buildSlotMetaMap(slots, (slot) => slot.key), [slots]);
	const [placementsByKey, setPlacementsByKey] = useState<Map<string, CanvasPlacement>>(new Map());
	const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);

	// Emit synchronously on change instead of via a publish effect: ref-pin the callback +
	// current slots, and mirror the map so the stable `commit` can read it without a render
	// round-trip. Every mutation goes through `commit`, keeping placementsRef authoritative.
	const onChangeRef = useRef(onPlacementsChange);
	onChangeRef.current = onPlacementsChange;
	const slotsRef = useRef(slots);
	slotsRef.current = slots;
	const placementsRef = useRef(placementsByKey);

	const commit = useCallback(
		(updater: (prev: Map<string, CanvasPlacement>) => Map<string, CanvasPlacement>) => {
			const next = updater(placementsRef.current);
			placementsRef.current = next;
			setPlacementsByKey(next);
			onChangeRef.current(toDto(slotsRef.current, next));
		},
		[]
	);

	const onPlacementChange = useCallback(
		(key: string, pageNum: number, pdfBox: PdfBox) => {
			commit((prev) => {
				const next = new Map(prev);
				next.set(key, { pageNum, pdfBox });
				return next;
			});
		},
		[commit]
	);

	const onPlaceAtDefault = useCallback(
		(key: string, pageNum: number) => {
			commit((prev) => {
				const offset = prev.size * 20;
				const next = new Map(prev);
				next.set(key, {
					pageNum,
					pdfBox: defaultBoxFor(
						showMetadata,
						offset,
						slotsRef.current.find((slot) => slot.key === key)?.isWitness
					),
				});
				return next;
			});
			setSelectedSlotKey(key);
		},
		[commit, showMetadata]
	);

	const onRemove = useCallback(
		(key: string) => {
			commit((prev) => {
				if (!prev.has(key)) {
					return prev;
				}
				const next = new Map(prev);
				next.delete(key);
				return next;
			});
			setSelectedSlotKey((current) => (current === key ? null : current));
		},
		[commit]
	);

	// On Show-details flip, rescale every placed box to the new aspect ratio (keep width,
	// recompute height) so placements match the submitted showStampMetadata; emit via commit.
	const prevShowMetadataRef = useRef<boolean | null>(null);
	useEffect(() => {
		if (prevShowMetadataRef.current === null) {
			prevShowMetadataRef.current = showMetadata;
			return;
		}
		if (prevShowMetadataRef.current === showMetadata) {
			return;
		}
		prevShowMetadataRef.current = showMetadata;
		commit((prev) => {
			const next = new Map<string, CanvasPlacement>();
			prev.forEach((value, key) => {
				// Per slot: a witness box takes the taller witness ratio.
				const isWitness =
					slotsRef.current.find((slot) => slot.key === key)?.isWitness ?? false;
				next.set(
					key,
					rescalePlacement(value, expectedAspectRatio(showMetadata, isWitness))
				);
			});
			return next;
		});
	}, [showMetadata, commit]);

	const isComplete = useMemo(
		() => isPlacementSetComplete(slots, (slot) => slot.key, placementsByKey),
		[slots, placementsByKey]
	);

	const hasOverlappingPlacements = useMemo(
		() => findOverlappingKeys(placementsByKey, []).size > 0,
		[placementsByKey]
	);

	return {
		slots,
		placementsByKey,
		slotByKey,
		selectedSlotKey,
		setSelectedSlotKey,
		onPlacementChange,
		onPlaceAtDefault,
		onRemove,
		isComplete,
		hasOverlappingPlacements,
	};
};
